import React from 'react'
import papa from 'papaparse'
import { useLazyQuery } from '@apollo/client'
import { RouteComponentProps } from 'react-router-dom'
import {
  getFormattedDateAndTime,
  SortDirectionEnum,
  TestResult,
  useStateWithDebounce,
  TestType,
  Lab,
  AppUsageAnalytics_appUsageAnalytics_surveys,
  useAppUsageAnalytics,
  AppUsageAnalytics as AppUsageAnalyticsCsv,
  AppUsageAnalyticsVariables as AppUsageAnalyticsVariablesCsv,
  SurveyStatus,
  AppUsageAnalytics_appUsageAnalytics_surveys_SurveyResponses,
  QuestionnaireQuestionKeys,
  getQuestionnaireResponsesSummary,
  UserPermissions,
  useAuth,
} from '@modmd/data'
import { useSortChangeHandler } from 'utils/helpers'
import { Downloading } from 'components/Downloading'
import DashboardPage from 'internal-portal/sharedComponents/DashboardPage'
import { AnalyticsAppUsageDatalist } from 'sharedComponents/AnalyticsAppUsageDatalist'
import { Box, Container } from 'components/Layout'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import { SelectInput } from 'components/SelectInput'
import { DateInput } from 'components/DateInput'
import { CompaniesDropdown } from 'sharedComponents/CompaniesDropdown'
import { GroupsDropdown } from 'sharedComponents/GroupsDropdown'
import { DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Button from 'components/Button'
import { RESULTS_PER_PAGE, CLEARANCE_STATUS_OPTIONS } from 'internal-portal/constants/filters'
import { APP_USAGE_ANALYTICS } from './operations'

type AnalyticsAppUsageProps = RouteComponentProps

type FilterState = {
  location?: string
  page?: string
  direction?: SortDirectionEnum
  sortBy?: string
  dateFrom?: string
  dateTo?: string
  memberLike?: string
  companyOrGroupLike?: string
  user?: string
  result?: TestResult | 'pending'
  companyId?: string
  groupId?: string
  barcode?: string
  lab?: Lab | null
  type?: TestType
  resultsPerPage?: string
  status?: SurveyStatus | ''
}

const AppUsageAnalytics: React.VFC<AnalyticsAppUsageProps> = () => {
  const { hasPermission } = useAuth()
  const isMobile = !useIsMinDevice(DEVICE.TABLET)
  const searchState = useSearchState<FilterState>()
  const { sorter, handleSortChange } = useSortChangeHandler()
  const [downloadableDataUrl, setDownloadableDataUrl] = React.useState<string>()
  const [download, setDownload] = React.useState(false)
  const today = new Date()
  const lastWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7)

  const appUsageQuery = useAppUsageAnalytics({
    filter: Object.entries({
      answeredBetween: [searchState.data.dateFrom || null, searchState.data.dateTo || null],
      companyIds: searchState.data.companyId ? [searchState.data.companyId] : undefined,
      groupIds: searchState.data.groupId ? [searchState.data.groupId] : undefined,
      status: searchState.data.status ? searchState.data.status : undefined,
    }).reduce((acc, [key, value]) => (value ? { ...acc, [key]: value } : acc), {}),
    sorter,
    page: Number(searchState.data.page) || 1,
    pageLength: Number(searchState.data.resultsPerPage) || Number(RESULTS_PER_PAGE[0].value),
  })

  const [filterData, setFilterData] = useStateWithDebounce<FilterState>({}, (filter) => {
    searchState.setSearchState({ ...filter, page: 1 }, false, true)
  })

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { value, name } = event.target
      setFilterData({ ...filterData, [name]: value })
    },
    [filterData, setFilterData]
  )

  const [getAppUsageCsv, { data: appUsageCsv, loading: appUsageCsvLoading }] = useLazyQuery<
    AppUsageAnalyticsCsv,
    AppUsageAnalyticsVariablesCsv
  >(APP_USAGE_ANALYTICS)

  const getClearance = (
    SurveyResponses: AppUsageAnalytics_appUsageAnalytics_surveys_SurveyResponses[]
  ) => {
    const isUserCleared = SurveyResponses.find(
      ({ key }) => QuestionnaireQuestionKeys.TEMPERATURE_IN_FAHRENHEIT === key
    )?.value
    if (isUserCleared) {
      const responsesSummary = getQuestionnaireResponsesSummary(SurveyResponses)
      const {
        isTemperatureInTolerance,
        hasOnlyNegativeAnswers,
        hasPositiveTestWithinLastTenDays,
        hasTraveled,
      } = responsesSummary

      if (isTemperatureInTolerance && hasOnlyNegativeAnswers && !hasPositiveTestWithinLastTenDays) {
        if (hasTraveled) {
          return 'Flagged'
        }
        return 'Cleared'
      }
      return 'Not cleared'
    }
    return 'No temperature found'
  }

  React.useEffect(() => {
    if (download) {
      const appUsage = appUsageCsv?.appUsageAnalytics.surveys.map(
        (survey: AppUsageAnalytics_appUsageAnalytics_surveys) => {
          const { createdAt, status, statusReason, SurveyResponses, User } = survey
          const clearance = getClearance(SurveyResponses)
          const statusReasonList = statusReason && statusReason.map((reason) => `${reason} `)
          const { Address, Groups, Companies } = User
          const userGroup = Groups.map((group) => ` ${group.id}-${group.name}`)
          const userCompany = Companies.map((company) => ` ${company.id}-${company.name}`)
          const patient = `${User.firstName || ''} ${
            User.lastName || ''
          } (${getFormattedDateAndTime(User.birthDate)})`
          const patientAddress = `${Address?.street || ''} ${
            Address?.city || ''
          } (${getFormattedDateAndTime(User.birthDate)})`
          const surveyLine = {
            answeredDate: createdAt,
            status: status || clearance,
            statusReason: statusReasonList,
            patient,
            patientAddress,
            patientGender: User.gender,
            patientRace: User.race,
            patientEthnicity: User.ethnicity,
            userGroup,
            userCompany,
          }
          return surveyLine
        }
      )
      if (appUsage && !downloadableDataUrl) {
        const csv = papa.unparse(appUsage, { header: true })
        const dataBlob = new Blob([csv], { type: 'text/csv' })

        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(dataBlob)
        link.setAttribute('download', `AppUsage-${Date()}.csv`)
        document.body.appendChild(link)
        link.click()
        setDownload(false)
      } else if (downloadableDataUrl) {
        window.URL.revokeObjectURL(downloadableDataUrl)
        setDownloadableDataUrl('')
        setDownload(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appUsageCsv])

  const handleDownloadCsv = () => {
    getAppUsageCsv({
      variables: {
        filterData: Object.entries({
          answeredBetween: [searchState.data.dateFrom || null, searchState.data.dateTo || null],
          companyIds: searchState.data.companyId ? [searchState.data.companyId] : undefined,
          status: searchState.data.status ? searchState.data.status : undefined,
        }).reduce((acc, [key, value]) => (value ? { ...acc, [key]: value } : acc), {}),
        pagination: {
          from: 0,
          length:
            appUsageQuery.data?.appUsageAnalytics.pagination.total ||
            Number(RESULTS_PER_PAGE[0].value),
          sortBy: 'id',
        },
      },
    })
    setDownload(true)
  }

  return (
    <DashboardPage
      Header={
        hasPermission([UserPermissions.DOWNLOAD_EVENT_CLEARANCE_ANALYTICS_CSV]) && (
          <Box display="flex" justifyContent="flex-end" p="1rem">
            <Button appearance="filled" onClick={handleDownloadCsv} disabled={appUsageCsvLoading}>
              {appUsageCsvLoading ? <Downloading /> : 'Download csv'}
            </Button>
          </Box>
        )
      }
    >
      <Container>
        <AnalyticsAppUsageDatalist
          surveys={appUsageQuery.data?.appUsageAnalytics.surveys || []}
          pagination={appUsageQuery.data?.appUsageAnalytics.pagination}
          isLoading={appUsageQuery.loading}
          tableHeader={
            <Box
              display="grid"
              gridGap="1rem"
              gridTemplateColumns={
                isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(13rem, max-content))'
              }
            >
              <DateInput
                id="dateFrom"
                name="dateFrom"
                label="Date From"
                value={filterData.dateFrom ? new Date(filterData.dateFrom) : lastWeek}
                onChange={(date) => {
                  setFilterData({
                    ...filterData,
                    dateFrom: date ? getFormattedDateAndTime(date) : undefined,
                  })
                }}
                hasBottomContent
                isFullWidth
              />
              <DateInput
                id="dateTo"
                name="dateTo"
                label="Date To"
                value={filterData.dateTo ? new Date(filterData.dateTo) : today}
                onChange={(date) => {
                  setFilterData({
                    ...filterData,
                    dateTo: date ? getFormattedDateAndTime(date) : undefined,
                  })
                }}
                hasBottomContent
                isFullWidth
              />
              <CompaniesDropdown
                value={searchState.data.companyId}
                onChange={(id) => {
                  setFilterData({ ...filterData, companyId: String(id), groupId: '' })
                }}
                hasBottomContent
              />
              <GroupsDropdown
                value={searchState.data.groupId}
                onChange={(id) => {
                  setFilterData({ ...filterData, groupId: String(id) })
                }}
                companyId={searchState.data.companyId}
                hasBottomContent
              />
              <SelectInput
                name="status"
                label="Clearance status"
                value={filterData.status || ''}
                onChange={handleInputChange}
                options={CLEARANCE_STATUS_OPTIONS}
                hasBottomContent
              />
              <SelectInput
                name="resultsPerPage"
                label="Results per page"
                value={filterData.resultsPerPage || ''}
                onChange={handleInputChange}
                options={RESULTS_PER_PAGE}
                hasBottomContent
              />
            </Box>
          }
          sorter={sorter}
          onSortChange={handleSortChange}
          requestDebounceInMs={250}
          onPageChange={(pageNumber) => {
            searchState.setSearchState({ page: pageNumber }, false)
          }}
          pageLength={appUsageQuery.data?.appUsageAnalytics.pagination.length}
        />
      </Container>
    </DashboardPage>
  )
}

export default AppUsageAnalytics
